// colDef/alumniColDef.tsx
import { Button } from "@/components/ui/button";
import { AlumniType } from "@/services/alumni.service";
import { createColumnHelper } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

const columnHelper = createColumnHelper<AlumniType>();

export type AlumniActionType = {
  id: string;
  name: string;
  action: "edit" | "delete";
};

export const createAlumniColumns = (
  onActionClick: (payload: AlumniActionType) => void
) => [
  // Name — clickable link to view page
  columnHelper.accessor("name", {
    header: "Name",
    cell: (info) => (
      <Link
        to={`/alumni/${info.row.original._id}`}
        className="font-medium hover:underline"
      >
        {info.getValue()}
      </Link>
    ),
  }),

  // Roll No — amber text
  columnHelper.accessor("rollNo", {
    header: "Roll No",
    cell: (info) => <span className="font-medium">{info.getValue()}</span>,
  }),

  // Batch — dark pill
  columnHelper.accessor("batch", {
    header: "Batch",
    cell: (info) => {
      const batch = info.getValue();
      if (!batch) return <span className="text-sm text-gray-400">—</span>;
      return (
        <span className="rounded-md border border-gray-300 px-2 py-0.5 text-xs font-medium">
          {batch}
        </span>
      );
    },
  }),

  // Dept — dark badge pill
  columnHelper.accessor("dept", {
    header: "Dept",
    cell: (info) => (
      <span className="rounded-md bg-gray-900 px-2.5 py-1 text-xs font-bold text-white">
        {info.getValue()}
      </span>
    ),
  }),

  // Email — plain
  columnHelper.accessor("email", {
    header: "Email",
    cell: (info) => {
      const email = info.getValue();
      return email ? (
        <span className="text-sm">{email}</span>
      ) : (
        <span className="text-sm text-gray-400">—</span>
      );
    },
  }),

  // Phone — plain
  columnHelper.accessor("phone", {
    header: "Phone",
    cell: (info) => {
      const phone = info.getValue();
      return phone ? (
        <span className="text-sm">{phone}</span>
      ) : (
        <span className="text-sm text-gray-400">—</span>
      );
    },
  }),

  // Inline edit / delete
  columnHelper.display({
    id: "actions",
    header: "Actions",
    cell: (info) => {
      const data = info.row.original;
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({ id: data._id, name: data.name, action: "edit" })
            }
            aria-label="Edit alumni"
          >
            <Pencil size={15} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({ id: data._id, name: data.name, action: "delete" })
            }
            aria-label="Delete alumni"
          >
            <Trash2 size={15} />
          </Button>
        </div>
      );
    },
  }),
];
